import { Controller } from '@hotwired/stimulus';

const PROGRESS_TICK_MS = 400;
const PROGRESS_CAP = 88;

export default class extends Controller {
  static targets = ['textarea', 'progress', 'progressBar', 'writeButton', 'badge'];
  static values = {
    url: String,
    force: Boolean,
    confirm: String
  };

  connect() {
    this.loading = false;
    this.progressTimer = null;
    this.progressPercent = 0;
    this.savedBlurb = '';
    this.syncButtonLabel();
  }

  disconnect() {
    this.stopProgressTimer();
  }

  async write(event) {
    event.preventDefault();
    if (this.loading) return;

    if (this.hasConfirmValue && this.confirmValue) {
      if (!window.confirm(this.confirmValue)) return;
    }

    this.savedBlurb = this.textareaTarget.value;
    this.startLoading();

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        credentials: 'same-origin',
        body: JSON.stringify({ force: this.forceValue })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Could not generate a blurb.');
      }

      await this.finishProgress();
      this.textareaTarget.value = data.blurb || '';
      this.syncButtonLabel();
      this.hideBadge();
      this.textareaTarget.dispatchEvent(new Event('input', { bubbles: true }));
    } catch (error) {
      this.textareaTarget.value = this.savedBlurb;
      this.syncButtonLabel();
      window.alert(error.message || 'Could not generate a blurb.');
      this.stopLoading();
    }
  }

  startLoading() {
    this.loading = true;
    this.progressPercent = 0;
    this.textareaTarget.value = '';
    this.textareaTarget.setAttribute('readonly', 'readonly');
    this.textareaTarget.setAttribute('aria-busy', 'true');
    this.textareaTarget.classList.add('nw-admin__blurb-textarea--loading');

    if (this.hasWriteButtonTarget) {
      this.writeButtonTarget.disabled = true;
      this.writeButtonTarget.setAttribute('aria-busy', 'true');
    }

    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove('hidden');
      this.progressTarget.setAttribute('aria-hidden', 'false');
    }

    this.updateProgressBar(4);
    this.stopProgressTimer();
    this.progressTimer = setInterval(() => this.tickProgress(), PROGRESS_TICK_MS);
  }

  stopLoading() {
    this.loading = false;
    this.stopProgressTimer();
    this.textareaTarget.removeAttribute('readonly');
    this.textareaTarget.removeAttribute('aria-busy');
    this.textareaTarget.classList.remove('nw-admin__blurb-textarea--loading');

    if (this.hasWriteButtonTarget) {
      this.writeButtonTarget.disabled = false;
      this.writeButtonTarget.removeAttribute('aria-busy');
    }

    if (this.hasProgressTarget) {
      this.progressTarget.classList.add('hidden');
      this.progressTarget.setAttribute('aria-hidden', 'true');
    }

    this.updateProgressBar(0);
  }

  tickProgress() {
    if (!this.loading) return;

    const remaining = PROGRESS_CAP - this.progressPercent;
    const step = Math.max(1, Math.round(remaining * 0.12));
    this.updateProgressBar(Math.min(PROGRESS_CAP, this.progressPercent + step));
  }

  async finishProgress() {
    this.stopProgressTimer();
    this.updateProgressBar(100);

    await new Promise((resolve) => {
      window.setTimeout(resolve, 320);
    });

    this.stopLoading();
  }

  stopProgressTimer() {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  }

  updateProgressBar(percent) {
    this.progressPercent = percent;
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`;
    }
  }

  syncButtonLabel() {
    if (this.loading) return;

    const hasBlurb = this.textareaTarget.value.trim().length > 0;
    this.forceValue = hasBlurb;
    this.confirmValue = hasBlurb ? 'Replace the current blurb with a new LLM draft?' : '';

    if (this.hasWriteButtonTarget) {
      this.writeButtonTarget.textContent = hasBlurb ? 'Rewrite blurb' : 'Write blurb';
    }

    if (hasBlurb) {
      this.hideBadge();
    } else if (this.hasBadgeTarget) {
      this.badgeTarget.classList.remove('hidden');
    }
  }

  hideBadge() {
    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.add('hidden');
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }
}
